import {
  animate,
  state,
  style,
  transition,
  trigger,
} from '@angular/animations';
import { DecimalPipe, isPlatformBrowser, NgFor, NgIf } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, EventEmitter, inject, Input, Output, PLATFORM_ID } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { UserArtistApiService } from '../../../services/API/apiServive/user-artist-api.service';
import { PaymentApiService } from '../../../services/API/apiPaymentService/payment-api.service';
import { ButtonComponent } from "../button/button.component";
import { Art, ArtList } from '../../../models/classes/art.model';
import { CanvasPreviewComponent } from '../canvas-preview/canvas-preview.component';
import { Album } from '../../../models/interfaces/response';
import { ExploreService } from '../../../services/API/explore.service/exploreservice';
import { Router } from '@angular/router';
import { RedirectManagerService } from '../../../services/state-management/redirect.service/redirect-manager.service';
import { PaymentConfirmationComponent } from '../../../modal/payment-confirmation/payment-confirmation.component';
import { PopupService } from '../../../services/popup/popup.service';
import { UserProfileService } from '../../../services/API/user-profile.service/user-profile.service';

@Component({
  selector: 'app-make-payment-multi-images',
  standalone: true,
  imports: [NgIf, NgFor, DecimalPipe, ButtonComponent, CanvasPreviewComponent],
  templateUrl: './make-payment-multi-images.component.html',
  styleUrl: './make-payment-multi-images.component.scss',
  animations: [
    trigger('slideInOut', [
      state(
        'in',
        style({
          transform: 'translateX(0)',
        })
      ),
      state(
        'out',
        style({
          transform: 'translateX(100%)',
        })
      ),
      transition('out => in', [animate('300ms ease-in')]),
      transition('in => out', [animate('300ms ease-out')]),
    ]),
  ],
})
export class MakePaymentMultiImagesComponent {
  @Input() art: Set<number> = new Set();
  @Input() arts: ArtList = new ArtList();
  @Input() isAlbumSubscription = false;
  @Input() albumDetails: Album | null = null;
  @Input() artId!: string; // ID of the art to be edited
  @Output() close = new EventEmitter<void>();
  @Output() artDeleted = new EventEmitter<void>();

  totalPrice: number = 0;
  slideState = 'out';
  availableCategories: string[] = [
    'Nature & Landscapes',
    'Architecture',
    'Astrophotography',
    'Sports',
    'Abstract',
    'People',
    'Paintings',
    'Urban & Cities',
    'Animal & Wildlife',
    'Street Photography',
    'Videos',
    'Kids Experiences',
  ];

  get getArts(): Art[] {
    if (this.albumDetails) {
      return ((this.albumDetails.artDetails as unknown) as Art[]);
    } else {
      return this.arts;
    }
  }

  constructor(
    private toaster: ToastrService,
    private userProfileService: UserProfileService,
    private apiService: UserArtistApiService,
    private apiPayment: PaymentApiService,
    private exploreService: ExploreService,
    private router: Router,
    private redirectService: RedirectManagerService,
    private popupService: PopupService,
  ) { }

  // Trigger sliding in
  ngOnInit(): void {
    setTimeout(() => {
      this.slideState = 'in';
    }, 0);

    if (this.albumDetails && this.albumDetails.albumPrice) {
      this.totalPrice = this.albumDetails.albumPrice
    } else {
      this.arts.map((art) => {
        this.totalPrice += parseFloat(art.price)
      })
      this.totalPrice = this.formatToTwoDecimals(this.totalPrice);
    }
  }

  // Method to format price to two decimals without rounding
  formatToTwoDecimals(value: string | number): number {
    const num = parseFloat(value.toString());
    if (!num) return 0;
    return parseFloat((num as any).toString().match(/^-?\d+(\.\d{0,2})?/)[0]);
  }

  closeModal(): void {
    this.slideState = 'out';
    setTimeout(() => this.close.emit(), 300); // Wait for the slide-out animation to finish
  }

  paymentLink: string = '';

  subscribe(paymentMethodId: string | null = null): void {
    if (this.albumDetails) {
      this.subscribeAlbum(paymentMethodId);
    } else {
      this.subscribeInAppPurchaseArtsSubscription(paymentMethodId);
    }
  }

  hasSavedCards = true;
  getCards(): void {
    this.userProfileService.getSavedCards()
      .subscribe({
        next: (res: any) => {
          const data = [...res.data];
          this.hasSavedCards = !!data.length;
        },
        complete: () => {
        },
      })
  }



  openConfirmationPopup(): void {
    if (!this.hasSavedCards) {
      this.subscribe();
      return;
    }
    if (this.art.size > 20) {
      this.toaster.warning("Select up to 20 arts to subscribe at a time")
      return;
    }
    this.popupService.open(PaymentConfirmationComponent, {
      totalPayable: this.totalPrice
    })
      ?.subscribe({
        next: (res) => {
          if (res) {
            this.popupService.close();
            this.subscribe(res.payload?.paymentMethodId || null);
          }
        }
      });
  }

  subscribeAlbum(paymentMethodId: string | null = null): void {
    this.exploreService.subscribeAlbum([(this.albumDetails as Omit<Album, "artDetails">).id], paymentMethodId)
      .subscribe({
        next: (res) => {
          this.paymentLink = res.data;
          if (this.paymentLink.includes("sub_")) {
            this.router.navigate([`/payment_success`], { queryParams: { stripeSubscriptionId: this.paymentLink } });
            return;
          }
          if (this.paymentLink) {
            this.redirectService.saveRoute('onSubscribe', this.router.url);
            this.triggerClick()
          }
        }
      })
  }

  subscribeInAppPurchaseArtsSubscription(paymentMethodId: string | null = null): void {
    this.apiPayment
      .subscribeInAppPurchaseArtsSubscription([...this.art], paymentMethodId)
      .subscribe({
        next: (res) => {
          if (res.message.includes("Already Purchase")) {
            this.toaster.warning(res.message);
            return;
          }
          this.paymentLink = res.data;
          if (this.paymentLink.includes("sub_")) {
            this.router.navigate([`/payment_success`], { queryParams: { stripeSubscriptionId: this.paymentLink } });
            return;
          }
          if (this.paymentLink) {
            this.triggerClick()
          }
        },
      });
  }

  platformId = inject(PLATFORM_ID)
  triggerClick() {

    if (isPlatformBrowser(this.platformId)) {
      const link = document.querySelector('#myLink') as HTMLAnchorElement;
      if (link && this.paymentLink) {
        link.href = this.paymentLink; // Ensure the `href` is dynamically updated.
        link.click();
      } else {
        console.error('Payment link is not ready.');
      }
    }
  }

  onSave(): void {
    this.apiService.deleteArt(this.artId).subscribe({
      next: (res: any) => {
        if (res.message == 'Subscribed art cannot be deleted') {
          this.toaster.info('Subscribed art cannot be deleted');
        } else {
          this.toaster.success(res.message || 'Art delete successfully!');
        }
        this.artDeleted.emit();
        this.closeModal();
      },
      error: (error) => {
        console.error('Failed to update art details:', error);
        this.toaster.success(
          error.message ||
          error.error.message ||
          'Failed to delete art. Please try again.'
        );
        // alert('Failed to update art details. Please try again.');
      },
    });
  }
}
